import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getGetProjectsProjectIdQueryKey,
  getGetProjectsQueryKey,
  type UpdateProjectMutationError,
  useUpdateProject,
} from '@/entities/project/api/generated/api';
import type {
  ErrorResponse,
  ProjectDetailResponse,
  ProjectUpdateRequest,
} from '@/entities/project/api/generated/model';
import { invalidateByPathPrefix } from '@/shared/lib/query/invalidateByPathPrefix';
import { toast } from '@/shared/ui';

/**
 * 事業詳細レスポンスから事業更新（PUT）リクエストの必須フィールドを組み立てる。
 *
 * 事業更新 API は必須フィールドが多いため、タグのみ更新する場合でも既存値を全て詰め直す。
 * 予算額は事業詳細・更新リクエストともに同一単位のため、単位変換は行わない。
 */
const buildUpdateRequest = (
  detail: ProjectDetailResponse,
  tagIds: string[],
): ProjectUpdateRequest => {
  const request: ProjectUpdateRequest = {
    kan_id: detail.kan.id,
    ko_id: detail.ko.id,
    moku_id: detail.moku.id,
    project_code: detail.project_code,
    project_name: detail.project_name,
    project_group_description: detail.project_group_description,
    project_description: detail.project_description,
    budget_management_division_id: detail.budget_management_division.id,
    budget_amount: detail.budget_amount,
    budget_description: detail.budget_description,
    year: detail.year,
    memo: detail.memo,
    tag_ids: tagIds,
  };

  if (detail.accounting_type?.id) request.accounting_type_id = detail.accounting_type.id;
  if (detail.saimoku_code) request.saimoku_code = detail.saimoku_code;
  if (detail.saimoku_name) request.saimoku_name = detail.saimoku_name;
  if (detail.saisaimoku_code) request.saisaimoku_code = detail.saisaimoku_code;
  if (detail.saisaimoku_name) request.saisaimoku_name = detail.saisaimoku_name;

  return request;
};

/**
 * 事業パネルのタグをインライン編集で更新するためのフック。
 *
 * タグ更新は事業編集の更新 API（useUpdateProject）を利用する（ITA-2721）。
 * 更新後は事業詳細・事業一覧・財源マッチング一覧（/projects 配下）のキャッシュを無効化し、
 * テーブルのタグ列表示も最新化する。
 */
export const useProjectTagsUpdate = (projectId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tagsLabel = t('project.fields.tags');

  const mutation = useUpdateProject({
    // インライン編集のエラーは onError のトーストで局所的に扱う（403/404 でもページ遷移はしない）。
    // suppressToast を付けないと axios インターセプタのグローバルトーストと二重表示になるため抑制する。
    request: { suppressToast: true },
    mutation: {
      onSuccess: () => {
        toast.success(t('common.messages.update-success', { name: tagsLabel }));
        queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetProjectsProjectIdQueryKey(projectId) });
        // 財源マッチング一覧（/projects/reference-matches/...）等のタグ列も最新化する
        invalidateByPathPrefix(queryClient, {
          include: ['/projects'],
          exclude: ['/projects/options', '/projects/eucs'],
        });
      },
      onError: (error: UpdateProjectMutationError) => {
        const errorResponse = error.response?.data as ErrorResponse | undefined;
        const message =
          errorResponse?.message ||
          error.message ||
          t('common.messages.update-failed', { name: tagsLabel });
        toast.error(message);
      },
    },
  });

  return {
    // 呼び出し元は await せず fire-and-forget で呼ぶため、Promise を返さない mutate を使う
    // （mutateAsync だと rejection が未処理となり unhandled promise rejection になる）。
    updateTags: (detail: ProjectDetailResponse, tagIds: string[]): void =>
      mutation.mutate({ projectId, data: buildUpdateRequest(detail, tagIds) }),
    isUpdating: mutation.isPending,
  };
};
